import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material"
import Datatable from "../../../components/features/Datatable";
import { fetchAllDisclaimers, deleteDisclaimer, fetchdisclaimerRoles } from "../../../slices/disclaimer";
import { DeleteIconButton, EditIconButton, EyeIconButton } from "../../../../assets/common-svg/CommonSvg";
import DeleteModal from "../../../components/common/BaseModal/DeleteModal";

const Disclaimer = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState();
    const [open, setOpen] = useState(false);
    const { AllDisclaimers } = useSelector(state => state.disclaimer);
    const navigate = useNavigate()
    const [rowData, setRowData] = useState(null)
    const [filters, setFilters] = useState({});

    const removeDisclaimer = async () => {
        if (!rowData?.id) return;

        try {
            await dispatch(deleteDisclaimer({ id: rowData.id })).unwrap();
            setOpen(false);
        } catch (error) {
            console.error("Delete failed:", error);
        }
    }
    useEffect(() => {
        dispatch(fetchdisclaimerRoles());
    }, [dispatch]);

    const columns = [
        {
            id: 'title',
            name: 'Title',
            align: "left",
            selector: row => row.title,
            sortable: true,
        },
        {
            id: 'disclaimer_message',
            align: "left",
            name: 'Disclaimer Message',
            selector: row => {
                const plainText = row.disclaimer_message?.replace(/<[^>]*>/g, '') || '';

                return (
                    <Typography
                        sx={{
                            maxWidth: '600px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {plainText}
                    </Typography>
                );
            },
            sortable: true
        },
        {
            id: 'permissions',
            align: "left",
            name: 'Permission',
            selector: row => {
                const roles = row.roles;
                if (!roles || roles.length === 0) return "N/A";
                return (
                    <Typography
                        sx={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            maxWidth: '300px',
                            py: 1
                        }}
                    >
                        {roles.map(role => role.title).join(", ")}
                    </Typography>
                );
            },
            sortable: true
        },
        {
            id: 'status',
            name: 'Status',
            align: "left",
            selector: row => {
                const statusValue = row.status === 0 ? "In-Active" : "Active";
                return statusValue === "Active" ?
                    <Typography className="text-[#369c50]">Active</Typography> :
                    <Typography className="text-[#FF2023]">In-Active</Typography>;
            },
            sortable: true
        },
        {
            name: 'Action',
            align: "center",
            selector: row => <Box key={row.id} sx={{
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'nowrap',  
    whiteSpace: 'nowrap',
    gap: '4px',
  }}>
                <Tooltip title="View" >
                    <span>
                        <IconButton
                            onClick={() => {
                                navigate(`add-disclaimer/view/${row.id}`, {
                                    state: [
                                        { name: "Disclaimer", path: location.pathname },
                                        {
                                            name: 'View Details',
                                            path: `add-disclaimer/view/${row.id}`,
                                        },
                                    ]
                                });
                            }}
                        >
                            <EyeIconButton
                                bgColor={"#3571FF"}
                            />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Edit" >
                    <span>
                        <IconButton
                            onClick={() => { navigate(`add-disclaimer/${row.id}`); }}
                        >
                            <EditIconButton
                                bgColor={"#C3C3C3"}
                            />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Delete" >
                    <span>
                        <IconButton
                            onClick={() => { setRowData(row), setOpen(true) }}
                        >
                            <DeleteIconButton
                                bgColor={"#FA5151"}
                            />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>,
        },
    ];

    const handleServerSideChange = async (params) => {
        const searchValue = params.search || '';
        const lowerSearch = searchValue.toLowerCase().trim();

        let titleSearch = '';
        let statusFilter = '';
        const isActive = lowerSearch === 'active' || 'active'.startsWith(lowerSearch) && lowerSearch.length >= 2;
        const isInactive =
            lowerSearch === 'inactive' ||
            lowerSearch === 'in-active' ||
            'inactive'.startsWith(lowerSearch.replace('-', '')) && lowerSearch.length >= 2 ||
            'in-active'.startsWith(lowerSearch) && lowerSearch.length >= 2;

        if (lowerSearch === '') {
            titleSearch = '';
            statusFilter = '';
        } else if (isInactive && !isActive) {
            statusFilter = 0;
            titleSearch = '';
        } else if (isActive && !isInactive) {
            statusFilter = 1;
            titleSearch = '';
        } else {
            titleSearch = searchValue;
            statusFilter = '';
        }
        let payload = {
            page: params.page,
            limit: params.per_page,
            title: titleSearch,
            status: statusFilter !== '' ? statusFilter : '',
            sort_by: params.sort_by || 'id',
            order_by: params.sort_order || 'desc'
        }
        try {
            setIsLoading(true)

            await dispatch(
                fetchAllDisclaimers(payload)
            )
        } catch (error) {
            console.error("Error fetching disclaimer list:", error)
        } finally {
            setIsLoading(false)
        }
    }
    const updateFilter = (params) => {
        const {
            page = 1,
            per_page = 10,
            search = '',
            ...appliedFilters
        } = params;

        const isFilterChanged = JSON.stringify(filters) !== JSON.stringify(appliedFilters);
        const finalPage = isFilterChanged ? 1 : page;

        setFilters(appliedFilters);

        handleServerSideChange({
            page: finalPage,
            per_page,
            search,
            sort_by: params.sort_by,
            sort_order: params.sort_order
        });
    }

    return <div className="!p-6">
        <div className="!p-4 mt-3 bg-white flex flex-col gap-4">
            <Datatable
                columns={columns}
                data={{
                    items: AllDisclaimers?.data || [],
                    total_count: AllDisclaimers?.page_details?.no_of_records || 0
                    
                }}
                title={"Disclaimer"}
                loading={isLoading}
                remote={true}
                onServerSideChange={updateFilter}
                searchPlaceholder="Search by Title/Status"
                BtnComponent={<Button className="ml-2 w-auto" onClick={() => (navigate('add-disclaimer'))}>Add New</Button>}
            />
            <DeleteModal
                open={open}
                setClose={setOpen}
                title={"Are you sure you want to Delete ?"}
                buttonText={"Delete"}
                handleSuccess={removeDisclaimer}
            />

        </div>
    </div>
}
export default Disclaimer;